import React, { useContext, useEffect, useState } from 'react';
import { Grid, IconButton, Card, CardHeader, CardContent, TextField, FormLabel, CircularProgress, InputAdornment, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Autocomplete } from '@material-ui/lab';
import moment from 'moment';

import DialogComp from '../../../../Core/Modal/dialogModal';
import AspectRatioIcon from '@material-ui/icons/AspectRatio';
import SearchIcon from '@material-ui/icons/Search';
import JobBookingDetailsList from './JobBookingDetailsList';
import formatters from '../../../../Core/Grid/inputFormatter';
import AppContext from '../../../../App/AppContext';

import { getBranchAndFranchise } from '../../../../Core/Service/branch-screen-service';
import { getJobBookingsByBranch_Month_Year } from '../../../../Core/Service/workflowJobs-service';

import { AppStorage } from '../../../../Core/Service/storage-service';
import TCardDashboardModal from '../Dashboard/T-cardModal';
import AddEditSiteVehicle from '../../Communicator/SiteVehicles/addEditSiteVehicle';
import { Tcard } from '../Dashboard/TcardTemplates/mainTcard';

const useStyles = makeStyles({
    yearInput: {
        '& .MuiOutlinedInput-inputMarginDense': {
            paddingTop: '4.1px',
            paddingBottom: '4.1px',
            color: 'white'
        }
    },
    filterItem: {
        flex: '1 1 0',
        minWidth: 0
    },
    filterCount: {
        marginLeft: '4px',
        fontSize: '11px',
        color: '#9e9e9e'
    },
    root: {
        minWidth: 275,
        minHeight: '175px',
        border: '1px solid black',
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.15s ease-in-out',
        '&:hover': {
            boxShadow: '0 3px 10px rgba(0,0,0,0.25)'
        }
    },
    todayRoot: {
        border: '2px solid #1976d2',
        boxShadow: '0 0 0 2px rgba(25,118,210,0.15)'
    },
    countBadge: {
        display: 'inline-block',
        minWidth: '20px',
        padding: '0px 6px',
        marginRight: '6px',
        borderRadius: '10px',
        background: 'rgba(255,255,255,0.9)',
        color: '#000',
        fontSize: '11px',
        fontWeight: 600,
        lineHeight: '18px',
        textAlign: 'center'
    },
    moreBar: {
        display: 'block',
        width: '100%',
        border: 'none',
        borderTop: '1px solid #d5d5d5',
        background: '#f0f6ff',
        color: '#1976d2',
        font: 'inherit',
        fontSize: '12px',
        fontWeight: 600,
        padding: '4px 0',
        cursor: 'pointer',
        '&:hover': {
            background: '#dceafd'
        }
    },
    cardContent: (props) => {
        const offset = props.fromTab ? 170 : 280;
        const cardContentHeight = Math.max(120, (window.innerHeight - offset) / 4 - 60);

        return {
            height: cardContentHeight,
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
                width: '0.3em'
            },
            '&::-webkit-scrollbar-track': {
                boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)'
            },
            '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#1976d2',
                outline: '1px solid slategrey'
            }
        };
    },
    cardRow: {
        padding: '3px',
        borderBottom: '1px dotted black',
        cursor: 'pointer',
        '&:nth-of-type(odd)': {
            background: '#E8E8E8'
        }
    },
    cardHeader: {
        height: '10px',
        backgroundColor: (props) => `${props.portalMenuBarBackgroundColour}`,
        border: (props) => `1px solid ${props.portalMenuBarBackgroundColour}`,
        color: 'white',
        textAlign: 'left',
        padding: '10px !important'
    },
    loaderContainer: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        height: '100%',
        zIndex: 99,
        background: 'grey',
        opacity: '0.6'
    }
});

const years = () => {
    const yearsList = [];
    const d = new Date();
    let startYear = d.getFullYear() - 10;
    let endYear = d.getFullYear() + 10;

    while (endYear - startYear >= 0) {
        yearsList.push({ description: `${startYear}`, id: `${startYear}` });
        startYear = startYear + 1;
    }
    return yearsList;
};

const monthsList = formatters.constantMonthList();

// Days can hold 30-40 jobs; only a few fit in a day card, the rest open in the day modal.
const MAX_VISIBLE_EVENTS = 5;

const getCustomerName = (booking) => booking?.cust || booking?.tCardsCustomerName || booking?.customerName || booking?.customer || '';

const matchesSearch = (booking, term) => {
    if (!term) return true;
    const reg = `${booking.registration || ''}`.toLowerCase();
    const cust = getCustomerName(booking).toLowerCase();
    const wip = `${booking.wipNumber || ''}`.toLowerCase();
    return reg.includes(term) || cust.includes(term) || wip.includes(term);
};

const JobBookingsScreen = (props) => {
    const { portalSettings, userRoles } = useContext(AppContext);
    const statusObject = JSON.parse(AppStorage.getTCardStatus() || '[]');

    const classes = useStyles({ ...props, ...portalSettings });
    const isCustomer = userRoles.includes('customer');

    const legendItems = [
        {
            label: portalSettings.portalSettingsCollectionDeliverText,
            background: portalSettings.portalSettingsCollectionDeliverBgColor || 'black',
            color: portalSettings.portalSettingsCollectionDeliverTextColor || 'white',
            st: 'cd'
        },
        {
            label: portalSettings.portalSettingsCustomerWaitingText,
            background: portalSettings.portalSettingsCustomerWaitingBgColor || 'black',
            color: portalSettings.portalSettingsCustomerWaitingTextColor || 'white',
            st: 'waiting'
        },
        {
            label: portalSettings.portalSettingsServiceText,
            background: portalSettings.portalSettingsServiceBgColor || 'black',
            color: portalSettings.portalSettingsServiceTextColor || 'white',
            st: 'service'
        },
        {
            label: portalSettings.portalSettingsInspectionText,
            background: portalSettings.portalSettingsInspectionBgColor || 'black',
            color: portalSettings.portalSettingsInspectionTextColor || 'white',
            st: 'inspection'
        },
        {
            label: portalSettings.portalSettingsMOTText,
            background: portalSettings.portalSettingsMOTBgColor || 'black',
            color: portalSettings.portalSettingsMOTTextColor || 'white',
            st: 'mot'
        },
        {
            label: portalSettings.portalSettingsRepairText,
            background: portalSettings.portalSettingsRepairBgColor || 'black',
            color: portalSettings.portalSettingsRepairTextColor || 'white',
            st: 'repair'
        },
        {
            label: portalSettings.portalSettingsNightShiftText,
            background: portalSettings.portalSettingsNightShiftBgColor || 'black',
            color: portalSettings.portalSettingsNightShiftTextColor || 'white',
            st: 'ns'
        },
        {
            label: portalSettings.portalSettingsRecallJobText,
            background: portalSettings.portalSettingsRecallJobBgColor || 'black',
            color: portalSettings.portalSettingsRecallJobTextColor || 'white',
            st: 'recall'
        },
        {
            label: portalSettings.portalSettingsTachoText,
            background: portalSettings.portalSettingsTachoBgColor || 'black',
            color: portalSettings.portalSettingsTachoTextColor || 'white',
            st: 'tacho'
        }
    ];

    const [state, setState] = useState({
        filterList: [],
        branchList: [],
        branchCode: {},
        monthCode: monthsList[`${new Date().getMonth()}`],
        yearCode: { description: `${new Date().getFullYear()}`, id: `${new Date().getFullYear()}` },
        currentMonthDays: [],
        customerList: [],
        customerCode: {},
        filterData: [],
        allData: [],
        showLoader: false,
        openModal: false,
        showDetailListModal: false,
        showSiteVehicleModal: false,
        headerRecordID: null,
        reg: null,
        wip: null,
        isReload: null,
        tCardID: null,
        tCardSelectedColor: null,
        selectedBookingList: [],
        selectedListDate: null,
        searchText: ''
    });

    const searchTerm = state.searchText.trim().toLowerCase();

    const visibleDays = state.filterData.map((day) => ({
        ...day,
        events: searchTerm ? day.events.filter((e) => matchesSearch(e, searchTerm)) : day.events
    }));

    const totalJobs = visibleDays.reduce((sum, day) => sum + day.events.length, 0);

    useEffect(() => {
        const fetchBranchData = async () => {
            try {
                const res = await getBranchAndFranchise();
                if (!res.success) return;

                const dashboardData = JSON.parse(localStorage.getItem('_dashboard_') || '{}');
                const selectedBranchName = dashboardData?.BranchPartReq?.trim();

                const branches = res.data.branches;
                const tempBranchList = branches.map((b) => ({
                    description: b.branchName,
                    id: b.branchID
                }));

                const matchedBranch = branches.find((b) => b.branchShortName?.trim() === selectedBranchName);

                setState((prevState) => ({
                    ...prevState,
                    branchList: tempBranchList,
                    branchCode: matchedBranch ? { description: matchedBranch.branchName, id: matchedBranch.branchID } : { ...tempBranchList[0] }
                }));
            } catch (error) {
                console.error('Failed to fetch branch data:', error);
            }
        };

        fetchBranchData();
    }, []);

    useEffect(() => {
        const fetchBookings = async () => {
            if (!state.branchCode?.id || !state.monthCode?.id || !state.yearCode?.id) return;
            setState((st) => ({
                ...st,
                showLoader: true
            }));
            let allData = [];
            let customerListUnique = [];

            try {
                const customerParam = state.customerCode?.id || '';
                const res = await getJobBookingsByBranch_Month_Year(state.branchCode.id, state.monthCode.id, state.yearCode.id, customerParam);
                if (res?.success) {
                    allData = Array.isArray(res.data) ? res.data : res.data?.list || [];
                }
                customerListUnique = [...new Set(allData.map(getCustomerName).filter(Boolean))].map((w) => {
                    return { description: w, id: w };
                });
            } catch (err) {
                console.error('Failed to fetch bookings:', err);
            }

            setState((st) => {
                let newSt = { ...st };
                newSt.customerList = customerListUnique;
                newSt.allData = allData;
                newSt.showLoader = false;
                return newSt;
            });
        };

        fetchBookings();
    }, [state.yearCode, state.monthCode, state.branchCode, state.customerCode, state.isReload]);

    useEffect(() => {
        if (!state.yearCode?.id || !state.monthCode?.id) return;
        let currentMonthDays = Array.from(Array(moment(`${state.yearCode.id}-${state.monthCode.id}`, 'YYYY-MM').daysInMonth()), (_, i) => {
            return { dayNo: i + 1, events: [] };
        });

        if (state.allData) {
            let tempCurrentMonthDays = [...currentMonthDays];
            state.allData.forEach((p) => {
                let bookingDay =
                    p.bookingDay !== undefined && p.bookingDay !== null
                        ? p.bookingDay
                        : p.bd
                          ? moment(p.bd).date()
                          : p.bookingDate
                            ? moment(p.bookingDate).date()
                            : null;
                let custName = getCustomerName(p);
                let index = tempCurrentMonthDays.findIndex((o) => o.dayNo == bookingDay);
                if (index >= 0) {
                    if (!state.customerCode?.id || state.customerCode.id === custName) {
                        tempCurrentMonthDays[index].events.push(p);
                    }
                }
            });
            setState((st) => {
                let newSt = { ...st };
                newSt.currentMonthDays = currentMonthDays;
                newSt.filterData = [...tempCurrentMonthDays];
                newSt.showLoader = false;
                return newSt;
            });
        }
    }, [state.allData, state.customerCode, state.yearCode, state.monthCode]);

    const handleAutoChange = (fieldName) => (e, val) => {
        setState((st) => {
            let newSt = { ...st };
            newSt[fieldName] = val;
            return newSt;
        });
    };

    const handleCardClick = (val) => {
        if (val?.tCardsID) {
            const tempStatusObj = statusObject.find((s) => s.id == val.sid);
            setState((st) => ({
                ...st,
                openModal: true,
                showSiteVehicleModal: false,
                tCardID: val.tCardsID,
                tCardSelectedColor: tempStatusObj?.bgc || null,
                selectedBookingData: val
            }));
        } else if (val) {
            setState((st) => ({
                ...st,
                openModal: false,
                showSiteVehicleModal: true,
                headerRecordID: val.headerRecordID,
                reg: val.registration,
                wip: val.wipNumber,
                selectedBookingData: val
            }));
        }
    };

    const showDetailModalHandler = (val) => {
        handleCardClick(val);
    };

    const handleTcardOpen = (cond, id, res, color) => {
        if (id) {
            setState((st) => ({
                ...st,
                openModal: cond,
                showSiteVehicleModal: false,
                tCardID: id,
                tCardSelectedColor: color
            }));
        }
    };

    const handleSiteVehicleClose = (res) => {
        setState((st) => ({
            ...st,
            showSiteVehicleModal: false,
            isReload: res ? new Date() : st.isReload
        }));
    };

    const handleTCardClose = (res) => {
        setState((st) => ({
            ...st,
            openModal: false,
            isReload: res ? new Date() : st.isReload
        }));
    };

    const showDetailListModalHandler = (list, date) => {
        setState((st) => ({
            ...st,
            showDetailListModal: true,
            selectedBookingList: list,
            selectedListDate: date
        }));
    };

    const closeDetailListModalHandler = () => {
        setState((st) => ({
            ...st,
            showDetailListModal: false,
            selectedBookingList: [],
            selectedListDate: null
        }));
    };

    return (
        <div style={{ padding: '10px', position: 'relative' }}>
            <Grid container>
                <Grid container item spacing={2} wrap="nowrap" alignItems="flex-end" style={{ marginBottom: '4px' }}>
                    <Grid item className={classes.filterItem}>
                        <FormLabel>Branch</FormLabel>
                        <Autocomplete
                            disableClearable
                            autoSelect
                            options={state.branchList}
                            name="branch"
                            value={state.branchCode}
                            onChange={handleAutoChange('branchCode')}
                            getOptionLabel={(option) => (typeof option === 'string' ? option : option.description || '')}
                            renderInput={(params) => <TextField margin="dense" fullWidth {...params} variant="outlined" />}
                        />
                    </Grid>
                    <Grid item className={classes.filterItem}>
                        <FormLabel>Select Month</FormLabel>
                        <Autocomplete
                            disableClearable
                            autoSelect
                            options={monthsList}
                            name="Month"
                            value={state.monthCode}
                            onChange={handleAutoChange('monthCode')}
                            getOptionLabel={(option) => (typeof option === 'string' ? option : option.description || '')}
                            renderInput={(params) => <TextField margin="dense" fullWidth {...params} variant="outlined" />}
                        />
                    </Grid>
                    <Grid item className={classes.filterItem}>
                        <FormLabel>Select Year</FormLabel>
                        <Autocomplete
                            disableClearable
                            autoSelect
                            options={years()}
                            name="Years"
                            value={state.yearCode}
                            onChange={handleAutoChange('yearCode')}
                            getOptionLabel={(option) => (typeof option === 'string' ? option : option.description || '')}
                            renderInput={(params) => <TextField margin="dense" fullWidth {...params} variant="outlined" />}
                        />
                    </Grid>
                    {!isCustomer && (
                        <Grid item className={classes.filterItem}>
                            <FormLabel>Select Customer</FormLabel>
                            <Autocomplete
                                autoSelect
                                options={state.customerList}
                                name="customer"
                                value={state.customerCode}
                                onChange={handleAutoChange('customerCode')}
                                getOptionLabel={(option) => (typeof option === 'string' ? option : option.description || '')}
                                renderInput={(params) => <TextField margin="dense" fullWidth {...params} variant="outlined" />}
                            />
                        </Grid>
                    )}
                    <Grid item className={classes.filterItem}>
                        <FormLabel>
                            Search
                            <span className={classes.filterCount}>
                                ({totalJobs} job{totalJobs === 1 ? '' : 's'}
                                {searchTerm ? ' matching' : ''})
                            </span>
                        </FormLabel>
                        <TextField
                            fullWidth
                            margin="dense"
                            variant="outlined"
                            placeholder="Reg, customer or WIP"
                            value={state.searchText}
                            onChange={(e) => {
                                const value = e.target.value;
                                setState((st) => ({ ...st, searchText: value }));
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" color="disabled" />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                </Grid>
                {state.showLoader && (
                    <Grid item sm={12} container alignItems="center" justify="center" className={classes.loaderContainer}>
                        <CircularProgress />
                    </Grid>
                )}
                <Grid container spacing={2}>
                    {visibleDays.map((o, i) => {
                        const dayLabel = `${i + 1} ${state.monthCode.description} ${state.yearCode.description}`;
                        const isToday = moment().isSame(moment(`${state.yearCode.id}-${state.monthCode.id}-${i + 1}`, 'YYYY-M-D'), 'day');
                        const hiddenCount = o.events.length - MAX_VISIBLE_EVENTS;
                        const shownEvents = o.events.slice(0, MAX_VISIBLE_EVENTS);
                        return (
                            <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={i}>
                                <Card className={`${classes.root} ${isToday ? classes.todayRoot : ''}`}>
                                    <CardHeader
                                        action={
                                            <>
                                                {o.events.length !== 0 && (
                                                    <Tooltip title={`Open all ${o.events.length} bookings`}>
                                                        <IconButton
                                                            style={{ padding: '0px', color: 'white' }}
                                                            onClick={() => showDetailListModalHandler(o.events, dayLabel)}
                                                        >
                                                            <AspectRatioIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </>
                                        }
                                        titleTypographyProps={{ variant: 'body1' }}
                                        title={
                                            <span>
                                                {o.events.length > 0 && <span className={classes.countBadge}>{o.events.length}</span>}
                                                {dayLabel}
                                            </span>
                                        }
                                        className={classes.cardHeader}
                                    />
                                    <CardContent style={{ padding: 0 }} className={classes.cardContent}>
                                        {o.events.length > 0 ? (
                                            shownEvents.map((k, kIdx) => {
                                                let tempStatusObj = statusObject.find((s) => s.id == k.sid);
                                                return (
                                                    <Grid
                                                        container
                                                        alignItems="center"
                                                        className={classes.cardRow}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleCardClick(k);
                                                        }}
                                                        key={k.tCardsID || k.id || kIdx}
                                                    >
                                                        <Grid item xs={12}>
                                                            <Tcard
                                                                hideBookingDate={true}
                                                                legendItems={legendItems}
                                                                handleTcardOpen={() => handleCardClick(k)}
                                                                date={k.bd || k.bookingDate}
                                                                customer={getCustomerName(k)}
                                                                isLoanVehicle={k.loanVehicle || k.tCardsLoanVehicle}
                                                                registration={k.registration}
                                                                bg={tempStatusObj?.bgc || '#f9f9f9'}
                                                                color={tempStatusObj?.bgc || '#f9f9f9'}
                                                                textColor={tempStatusObj?.tc || '#000000'}
                                                                tCardsID={k.tCardsID || null}
                                                                isTCard={Boolean(k.tCardsID)}
                                                                breakdown={k.breakdown}
                                                                breakdownStatus={k.breakdownStatus}
                                                                breakdownTime={k.breakdownTime}
                                                                status={{
                                                                    recall: k.tCardsIsRecallJob,
                                                                    tacho: k.tCardsIsTacho,
                                                                    motloc: k.tCardsMotloc,
                                                                    mot: k.tCardsIsMOT,
                                                                    repair: k.tCardsIsRepair,
                                                                    inspection: k.tCardsIsInspection,
                                                                    cd: k.tCardsIsCollectionDeliver,
                                                                    waiting: k.tCardsIsCustomerWaiting,
                                                                    service: k.tCardsIsService,
                                                                    ns: k.tCardsIsNightShift
                                                                }}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                );
                                            })
                                        ) : (
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    justifyContent: 'center',
                                                    height: '100%'
                                                }}
                                            >
                                                <div style={{ marginLeft: 'auto', marginRight: 'auto', color: '#D0D0D0', cursor: 'default' }}>
                                                    {searchTerm ? 'No matches' : 'No bookings'}
                                                </div>
                                            </div>
                                        )}
                                    </CardContent>
                                    {hiddenCount > 0 && (
                                        <button type="button" className={classes.moreBar} onClick={() => showDetailListModalHandler(o.events, dayLabel)}>
                                            +{hiddenCount} more
                                        </button>
                                    )}
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            </Grid>

            {state.showDetailListModal ? (
                <DialogComp
                    title={`Bookings on ${state.selectedListDate} (${state.selectedBookingList.length})`}
                    maxWidth="lg"
                    onClose={closeDetailListModalHandler}
                    fullWidth
                    fullScreen
                    style={{ zIndex: 1250 }}
                >
                    <JobBookingDetailsList selectedBookingList={state.selectedBookingList} handleCardClick={handleCardClick} />
                </DialogComp>
            ) : null}

            {state.openModal ? (
                <TCardDashboardModal id={state.tCardID} onClose={handleTCardClose} tCardSelectedColor={state?.tCardSelectedColor} />
            ) : null}
            {state.showSiteVehicleModal ? (
                <AddEditSiteVehicle
                    onClose={handleSiteVehicleClose}
                    headerRecordID={state.headerRecordID}
                    reg={state.reg}
                    wip={state.wip}
                    isShowHistory={true}
                />
            ) : null}
        </div>
    );
};

export default JobBookingsScreen;
